import useCache from "@hooks/useCache";
import useCacheKey from "@hooks/useCacheKey";
import { useCallback } from "react";

type SavedPodcastsData = {
	savedPodcastIds: number[];
};

const defaultData: SavedPodcastsData = {
	savedPodcastIds: [],
};

export const useSavedPodcastsCache = () => {
	const { userCacheKey } = useCacheKey();

	const { data, setCache, isLoading, isUpdating } = useCache({
		key: userCacheKey ? `${userCacheKey}:feature-podcast` : "",
		defaultData,
		shouldOptimisticUpdate: true,
		enabled: !!userCacheKey,
	});

	const addPodcast = useCallback(
		async (id: number) => {
			if (data?.savedPodcastIds.includes(id)) {
				return;
			}
			const newIds = [...(data?.savedPodcastIds || []), id];
			await setCache({ savedPodcastIds: newIds });
		},
		[data?.savedPodcastIds, setCache],
	);

	const removePodcast = useCallback(
		async (id: number) => {
			const newIds = (data?.savedPodcastIds || []).filter(
				(savedId) => savedId !== id,
			);
			await setCache({ savedPodcastIds: newIds });
		},
		[data?.savedPodcastIds, setCache],
	);

	const isPodcastSaved = useCallback(
		(id: number): boolean => {
			return data?.savedPodcastIds.includes(id) ?? false;
		},
		[data?.savedPodcastIds],
	);

	const toggleSavedPodcast = useCallback(
		async (id: number) => {
			if (isPodcastSaved(id)) {
				await removePodcast(id);
			} else {
				await addPodcast(id);
			}
		},
		[isPodcastSaved, removePodcast, addPodcast],
	);

	return {
		savedPodcastIds: data?.savedPodcastIds || [],
		addPodcast,
		removePodcast,
		isPodcastSaved,
		toggleSavedPodcast,
		isLoading,
		isUpdating,
	};
};
